import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, input, ChangeDetectionStrategy } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BillableMoovDataReport } from '../../../models/billableMoovDataReport';
import { DatePipe, formatDate } from '@angular/common';
import { MoovService } from '../../../services/MoovService';
import { BillableMoovData } from '../../../models/billableMoovData';
import { of, take } from 'rxjs';
import { Customer } from '../../../models/customer';
import { MatSpinnerOverlayModule } from '../../../shared/spinner/mat-spinner-overlay.module';

@Component({
  standalone:true,
  imports: [BaseChartDirective, MatSpinnerOverlayModule],
  selector: 'app-report-chart',
  templateUrl: './report-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./report-chart.component.scss']
})
export class ReportChartComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;
  readonly timeframe = input<number>();
  readonly customer = input<Customer>();

  public busy: boolean = true;
  private _dataSource = new Map<string, BillableMoovDataReport>();
  private fromDate!: number;

  constructor(private moovService: MoovService, private cd: ChangeDetectorRef, private datePipe: DatePipe) { }

  ngOnInit(): void {
    setTimeout(() => {
      this.cd.detectChanges();
      this.resetReport();
      this.loadReportData();
      this.chart?.render();
    }, 0);
  }

  get dataSource() {
    return this._dataSource;
  }

  private loadReportData(timeFrame?: number) {
    this.busy = true;
    this.fromDate = timeFrame ? timeFrame : 7; //set default of 7 days if no timeFrame is specified
    let mappedData = new Map<string, BillableMoovDataReport>();
    let today = new Date();
    let timeFrameDate = new Date(new Date().setDate(new Date().getDate() - this.fromDate));

    //load the year to date data
    if (this.fromDate == 365) {
      for (let i = 365; i > 0; i--) {
        let month = formatDate(new Date(new Date().setDate(new Date().getDate() - i)), 'MM-01-yyyy', 'en-US'); //need to add constant day for datePipe to accept format

        if (!mappedData.has(month)) mappedData.set(month, new BillableMoovDataReport); //set key to each month within timeframe selected
      }

      this.moovService.getBillableMoovData(timeFrameDate, today, this.customer()?.id).pipe(take(1)).subscribe((r) => {
        this.processYearlyReportData(r, mappedData);
      });

    //load the last 14 days
    } else if(this.fromDate == 14) {
      for (let i = 0; i < this.fromDate; i++) {
        let date = formatDate(new Date(new Date().setDate(new Date().getDate() - i)), 'MM-dd-yyyy', 'en-US');
        mappedData.set(date, new BillableMoovDataReport);
      }
      mappedData = new Map([...mappedData.entries()].reverse());
      this.moovService.getBillableMoovData(timeFrameDate, today, this.customer()?.id).pipe(take(1)).subscribe((r) => {
        this.process2WeeksReportData(r, mappedData);
      }); 
    //load the last 7 days
    } else if(this.fromDate == 7) {
      for (let i = 0; i < this.fromDate; i++) {
        let date = formatDate(new Date(new Date().setDate(new Date().getDate() - i)), 'MM-dd-yyyy', 'en-US');
        mappedData.set(date, new BillableMoovDataReport);
      }
      mappedData = new Map([...mappedData.entries()].reverse());
      this.moovService.getBillableMoovData(timeFrameDate, today, this.customer()?.id).pipe(take(1)).subscribe((r) => {
        this.processDailyReportData(r, mappedData);
      });
    }
  }

  //process data for Last 7 Days
  private processDailyReportData(rdArray: BillableMoovData[], rdMap: Map<string, BillableMoovDataReport>) {
    for (let key of rdMap.keys()) {
      rdArray.forEach(data => {
        if (data.formatDateMonth == key) {
          let billableReport = new BillableMoovDataReport();
          billableReport.setCompletedAt(data.formatDateMonth);
          billableReport.setRecordCount(rdMap.get(data.formatDateMonth)!.getRecordCount() + data.sourceRecordCount);
          billableReport.setMoovCount(rdMap.get(data.formatDateMonth)!.getMoovCount() + 1);
          rdMap.set(data.formatDateMonth, billableReport);
        }
      });
    }
    this.renderChartData(rdMap);
  }

  //process data for Last 14 Days
  private process2WeeksReportData(rdArray: BillableMoovData[], rdMap: Map<string, BillableMoovDataReport>) {
    for (let key of rdMap.keys()) {
      rdArray.forEach(data => {
        if (data.formatDateMonth == key) {
          let billableReport = new BillableMoovDataReport();
          billableReport.setCompletedAt(data.formatDateMonth);
          billableReport.setRecordCount(rdMap.get(data.formatDateMonth)!.getRecordCount() + data.sourceRecordCount);
          billableReport.setMoovCount(rdMap.get(data.formatDateMonth)!.getMoovCount() + 1);
          rdMap.set(data.formatDateMonth, billableReport);
        }
      });
    }
    this.renderChartData(rdMap);
  }

  //process data for Last 12 Months
  private processYearlyReportData(rdArray: BillableMoovData[], rdMap: Map<string, BillableMoovDataReport>) {
    for (let key of rdMap.keys()) {
      rdArray.forEach(data => {
        if (data.formatDateDay== key) {
          let billableReport = new BillableMoovDataReport();
          billableReport.setCompletedAt(data.formatDateDay);
          billableReport.setRecordCount(rdMap.get(data.formatDateDay)!.getRecordCount() + data.sourceRecordCount);
          billableReport.setMoovCount(rdMap.get(data.formatDateDay)!.getMoovCount() + 1);
          rdMap.set(key, billableReport); 
        }
      });
    }
    this.renderChartData(rdMap);
  }

  private renderChartData(rdMap: Map<string, BillableMoovDataReport>) {
    this.busy = true;
    this._dataSource = rdMap;

    for (let key of this._dataSource.keys()) {
      let dateFormat = key.replace(/(\d{2})-(\d{2})-(\d{4})/, "$1/$2/$3"); //date format to replace hyphens to work in Firefox
      if (this.fromDate == 365) {
        
        let date = this.datePipe.transform(dateFormat, 'MMM yyyy', 'en-US'); //format label to month name and year
        this.barChartData.labels!.push(date);
      } else {
        let date = this.datePipe.transform(dateFormat, "MM/dd/yyyy", 'en-US');
        this.barChartData.labels!.push(date);
      }
      this.barChartData.datasets[0].data.push(this._dataSource.get(key)!.getRecordCount());
      this.barChartData.datasets[1].data.push(this._dataSource.get(key)!.getMoovCount());
    }

    //dynamically adjust max tick size
    // this.barChartOptions.scales['right-y-axis'].max = Math.max(...this.barChartData.datasets[0].data) + 40;
    // this.barChartOptions.scales['left-y-axis'].max = Math.max(...this.barChartData.datasets[1].data) + 2;

    this.chart?.render();
    this.busy = false;
    this.cd.detectChanges();
  }

  private resetReport() {
    this.barChartData.labels = [];
    this.barChartData.datasets[0].data = [];
    this.barChartData.datasets[1].data = [];
    this._dataSource = new Map();
  }

  public onTimeframeChange(value: number) {
    this.resetReport();
    this.loadReportData(value);
    this.chart?.update();
  }

  public barChartType: ChartType = 'bar';

  public barChartPlugins = [
    DataLabelsPlugin
  ];

  public barChartData : ChartConfiguration['data'] =  {
    //x-axis labels
    labels: [],
    datasets: [
      {
        data:
          [],
        label: 'records processed',
        borderColor: '#aaafb8',
        backgroundColor: '#aaafb8',
        pointBackgroundColor: '#aaafb8',
        pointHoverBorderColor: '#aaafb8',
        type: 'line' as const,
        yAxisID: 'right-y-axis',

      },
      {
        data:
          [],
        label: 'moovs executed',
        backgroundColor: '#618c50',
        borderColor: '#618c50',
        hoverBackgroundColor: '#a5c199',
        type: 'bar' as const,
        yAxisID: 'left-y-axis',
      }
    ]
  }

  public barChartOptions: ChartConfiguration['options'] = {
    elements: {
      line: {
        tension: 0.2
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
      },
      'left-y-axis': {
        type: 'linear' as const,
        position: 'left',
        min: 0,
        ticks: {
          color: '#618c50',
          precision:0
        },

      },
      'right-y-axis': {
        type: 'linear' as const,
        position: 'right',
        ticks: {
          precision:0
        },
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      },
      datalabels: {
        display: false
      },
      tooltip: {
        backgroundColor: '#3a4353'
      }
    },
    animation: false
  }

  ngOnDestroy(): void {
    this.chart?.chart?.destroy();
  }
}